import React from 'react';

import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import { CitationSource, DocumentCitationSource, ToolCitationSource, SourcesPanelProps } from './MessagesContainer.types';
import {
    SOURCE_ANCHOR_PREFIX,
    SOURCES_GROUP_DOCUMENTS,
    SOURCES_GROUP_INGESTED,
    CITATION_SOURCE_TYPE,
    CITATION_SURFACE,
} from './MessagesContainer.constants';
import { buildCanonicalMap, buildDisplayMap, citationActionTitle } from './citationUtils';

import styles from './MessagesContainer.module.scss';

/**
 * Renders the citation sources panel below an assistant answer bubble.
 * Grouped: Documents → AICA Report (Ingested Data) [all tool/data sources].
 * model_knowledge is NEVER rendered — model reasoning is not surfaced as a
 * reference (product decision, 2026-07-13). Internal server names
 * (Polus/Underwriting/…) are never surfaced either.
 * Entries carry reading-order display numbers ([1] = first reference in the
 * answer) and are sorted by them within each group.
 * Renders nothing when sources is empty or undefined (back-compat).
 * Entries are activatable via the SAME shared handler as CitationMarker (D6)
 * so a panel click and its inline marker never diverge.
 */
const SourcesPanel: React.FC<SourcesPanelProps> = ({
    msgId,
    sources,
    canonicalSources: canonicalProp,
    displayNumbers,
    onCitationActivate,
    getCitationTitle,
}) => {
    // Use the parent's pre-computed list when available; else derive from raw sources.
    let canonicalSources = canonicalProp;
    let numbers = displayNumbers;
    if (!canonicalSources) {
        const canonical = buildCanonicalMap(sources);
        const display = buildDisplayMap(undefined, canonical.idToCanonical, canonical.canonicalSources);
        canonicalSources = display.orderedSources;
        numbers = display.displayNumbers;
    }
    if (!canonicalSources.length) return null;

    const shownNumber = (s: CitationSource) => numbers?.[s.id] ?? s.id;
    const byDisplay = (a: CitationSource, b: CitationSource) => Number(shownNumber(a)) - Number(shownNumber(b));

    // model_knowledge is excluded defensively even if a caller passes it through.
    const documents = canonicalSources.filter((s): s is DocumentCitationSource => s.type === CITATION_SOURCE_TYPE.DOCUMENT).sort(byDisplay);
    const tools = canonicalSources.filter((s): s is ToolCitationSource => s.type === CITATION_SOURCE_TYPE.TOOL).sort(byDisplay);

    if (documents.length === 0 && tools.length === 0) return null;

    const activate = (s: CitationSource) => {
        onCitationActivate?.(s, CITATION_SURFACE.PANEL, {
            msgId,
            canonicalId: s.id,
            sourcesCount: canonicalSources?.length,
            shownNumber: Number(shownNumber(s)),
        });
    };

    const entryProps = (s: CitationSource) =>
        onCitationActivate
            ? {
                  onClick: () => activate(s),
                  role: 'button' as const,
                  tabIndex: 0,
                  title: getCitationTitle ? getCitationTitle(s) : citationActionTitle(s),
                  onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                          // Space would otherwise scroll the page since this div isn't natively interactive.
                          e.preventDefault();
                          activate(s);
                      }
                  },
              }
            : {};

    return (
        <div className={styles.SourcesPanel} data-testid="sources-panel">
            <span className={styles.SourcesPanelLabel}>Sources</span>

            {documents.length > 0 && (
                <div className={styles.SourcesGroup}>
                    <span className={styles.SourcesGroupLabel}>{SOURCES_GROUP_DOCUMENTS}</span>
                    {documents.map((s) => (
                        <div
                            key={s.id}
                            id={`${SOURCE_ANCHOR_PREFIX}${msgId}-${s.id}`}
                            className={styles.Source}
                            data-testid={`source-entry-${s.id}`}
                            {...entryProps(s)}
                        >
                            <span className={styles.SourceIndex}>[{shownNumber(s)}]</span>
                            <span className={styles.SourceName}>{s.file_name}</span>
                            {s.as_of && <span className={styles.SourceAsOf}>{s.as_of}</span>}
                        </div>
                    ))}
                </div>
            )}

            {tools.length > 0 && (
                <div className={styles.SourcesGroup}>
                    <span className={styles.SourcesGroupLabel}>{SOURCES_GROUP_INGESTED}</span>
                    {tools.map((s) => (
                        <div
                            key={s.id}
                            id={`${SOURCE_ANCHOR_PREFIX}${msgId}-${s.id}`}
                            className={styles.Source}
                            data-testid={`source-entry-${s.id}`}
                            {...entryProps(s)}
                        >
                            <span className={styles.SourceIndex}>[{shownNumber(s)}]</span>
                            <span className={styles.SourceName}>{s.display_name}</span>
                            {s.as_of && <span className={styles.SourceAsOf}>{s.as_of}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SourcesPanel;
